import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, CardHeader, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { asText } from "@/hq/asText";

const STAGES = ["NEW","RESEARCHING","SCANNED","QUALIFIED","CAMPAIGN_READY","CONTACTED","ENGAGED","OPPORTUNITY","QUOTE","WON","LOST","DO_NOT_CONTACT"];

export default function HQSalesProspectDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanBusy, setScanBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await hqApi.get(`/sales/prospects/${id}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function scanNow() {
    setScanBusy(true);
    try {
      const { data: r } = await hqApi.post(`/sales/prospects/${id}/scan`);
      toast.success(`Scan complete — Orange Score ${r.scan?.orange_score ?? "—"}/100`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Scan failed");
    } finally { setScanBusy(false); }
  }

  async function setStage(stage) {
    try {
      await hqApi.patch(`/sales/prospects/${id}/stage`, { stage });
      toast.success(`Stage → ${stage}`); load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }

  async function archive() {
    if (!window.confirm("Archive this prospect? History is preserved.")) return;
    try {
      await hqApi.post(`/sales/prospects/${id}/archive`);
      toast.success("Archived."); load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }

  if (loading) return <Skeleton h={200} />;
  if (!data) return <EmptyState title="Not found" testId="prospect-detail-empty" />;
  const p = data.prospect;
  const diag = p.diagnosis;
  const opp = p.orange_opportunity_score;

  return (
    <div className="space-y-5" data-testid="prospect-detail-root">
      <Card>
        <div className="px-6 pt-6 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Prospect</div>
            <h1 className="mt-1 font-black text-2xl">{p.business_name || p.contact_name || "—"}
              {p.is_demo && <span className="ml-2 px-2 py-1 rounded bg-[#0A0A0A] text-white text-[10px] tracking-[0.2em] uppercase">DEMO</span>}
            </h1>
            <div className="text-[13px] text-[#4A4A4A]">{p.contact_name} · {p.normalized_email} · {p.normalized_domain}</div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={scanNow}
              disabled={scanBusy}
              className="px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[12px] font-black tracking-[0.12em] uppercase disabled:opacity-50"
              data-testid="prospect-scan-now"
            >{scanBusy ? "Scanning…" : "Scan now"}</button>
            <button onClick={archive} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[12px] font-bold tracking-[0.12em] uppercase" data-testid="prospect-archive">Archive</button>
          </div>
        </div>
        <div className="px-6 pb-6 flex flex-wrap gap-2 items-center">
          <span className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Stage</span>
          <select
            value={p.stage}
            onChange={(e) => setStage(e.target.value)}
            data-testid="prospect-stage-select"
            className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[12px] font-bold tracking-[0.12em]"
          >
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="ml-3 text-[11px] text-[#8A8A8A]">Compliance:
            {p.opt_out && " opt-out"}
            {p.suppressed && " suppressed"}
            {p.do_not_contact && " DNC"}
            {!p.opt_out && !p.suppressed && !p.do_not_contact && " OK"}
          </span>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader eyebrow="Sales Diagnosis" title="From latest scan" />
          <div className="px-6 pb-6 pt-3 text-[13px]">
            {!diag ? (
              <p className="text-[#8A8A8A]" data-testid="diagnosis-empty">Run a scan to generate a diagnosis.</p>
            ) : (
              <div className="space-y-3" data-testid="diagnosis-block">
                {diag.primary && (
                  <div className="p-3 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7]">
                    <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">Primary opportunity</div>
                    <div className="font-black">{diag.primary.service}</div>
                    <div className="text-[12px] text-[#4A4A4A]">{diag.primary.reason}</div>
                    <div className="mt-1 text-[10px] tracking-[0.2em] uppercase font-bold text-[#8A8A8A]">Confidence: {Math.round((diag.primary.confidence || 0) * 100)}%</div>
                  </div>
                )}
                {diag.secondary && (
                  <div className="p-3 rounded-xl border border-[#EAEAEA]">
                    <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Secondary</div>
                    <div className="font-bold">{diag.secondary.service}</div>
                    <div className="text-[12px] text-[#4A4A4A]">{diag.secondary.reason}</div>
                  </div>
                )}
                {diag.reasons?.length ? (
                  <ul className="list-disc pl-5 text-[12px] text-[#4A4A4A] space-y-0.5">
                    {diag.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                ) : null}
                <p className="text-[11px] text-[#8A8A8A]">
                  CRM &amp; Business System is intentionally excluded — it requires human qualification.
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader eyebrow="Orange Opportunity Score" title={opp ? `${opp.score}/100 · ${opp.band}` : "Not scored"} />
          <div className="px-6 pb-6 pt-3 text-[13px]">
            {!opp ? (
              <p className="text-[#8A8A8A]" data-testid="opp-score-empty">Score becomes available after the first scan.</p>
            ) : (
              <div className="space-y-2" data-testid="opp-score-block">
                <div className="flex items-center gap-3">
                  <div className="font-black text-4xl">{opp.score}</div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#FF6A00]">{opp.band}</div>
                </div>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mt-4">Contributing factors</div>
                <ul className="text-[12px] text-[#4A4A4A] divide-y divide-[#EAEAEA]">
                  {(opp.factors || []).map((f, i) => (
                    <li key={i} className="py-1.5 flex justify-between">
                      <span>{f.key.replace(/_/g, " ")}</span>
                      <span className="font-bold">+{f.contribution}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader eyebrow="Activity" title="Timeline" />
        <div className="px-6 pb-6 pt-2">
          {!data.activities?.length ? (
            <EmptyState title="No activity" testId="prospect-activity-empty" />
          ) : (
            <ul className="divide-y divide-[#EAEAEA]" data-testid="prospect-activity-list">
              {data.activities.map((a) => (
                <li key={a.id} className="py-3 text-[13px]">
                  <div className="font-semibold">{asText(a.description) || asText(a.event_type)}</div>
                  <div className="text-[11px] text-[#8A8A8A]">{asText(a.event_type)} · {a.created_at?.slice(0, 19)?.replace("T", " ")}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}
